import { ITenantContractContext } from "./contexts/TenantContractContext";
import { PersonTenant, TCCommercialInfo, TenancyContract, Tenant, TenantTerminateRequest, } from "@eres/ejari-api/Entity/tenant";
import { EjariPropertyType } from "@eres/ejari-api/Entity";
import { EjariCoreConfig, TenancyContactAction, TenantRequestAction, TenantRequestStatus, TenantRequestUserType } from "./constants";
import { normalizeTenantContract, normalizeTenantTerminate } from "@eres/ejari-api/ContractUtils";
import { Util } from "@eres/core";
import { isEmptyStringOrNull } from "@eres/core/dist/StringUtils";
import { getAppServices } from "@10x/coreapp";
import { MoveOut } from "@eres/ejari-api/Entity/contract";

export function buildTenancyContract( ctx: ITenantContractContext ): TenancyContract {
	const property = ctx.property!;
	const propertyId = property.PropertyTypeId === EjariPropertyType.UNIT ? property.UnitEjariPropertyId :
		property.VillaEjariPropertyId;
	const tenantId = getAppServices().storeService.getStore().getState().User.tenantId;
	let contract: TenancyContract = {
		EjariPropertyId: [
			Number( propertyId ),
		],
		PrimaryOwnerParticipantId: 0,
		ActiveUserType: TenantRequestUserType.TENANT,
		PaymentAction: 22,
		ActiveUserID: "" + tenantId,
		Identity: 0,
		ContractValueType: 1,
		TenancyContractId: ( ctx.action === TenancyContactAction.RENEW_CONTRACT ) ? ctx.contract!.TenancyContractId : 0,
		RequestType: 39,
		TempContractId: ctx.tempContractId,
	};
	contract = {
		...contract,
		...getTenantData( ctx, tenantId! ),
		...getContractData( ctx ),
		...getHazardsMaterialData( ctx ),
		Remarks: ctx.remarksInfo!.Remarks,
		...( !ctx.isCommercial && { TotalCoTenants: ctx.coOccupantsInfo?.TotalCoTenants } ),
	};

	if ( ctx.action === TenancyContactAction.NEW_CONTRACT ) {
		contract = {
			...contract,
			...getMoveOutData( ctx ),
		};
	}
	contract = normalizeTenantContract( contract );
	return contract;
}

function getMoveOutData( ctx: ITenantContractContext ): Partial<TenancyContract> {
	const cd: MoveOut | undefined = ctx.moveOut!;
	let data: Partial<TenancyContract> = {};
	if ( cd ) {
		data = {
			...Util.subSet( cd, "DewaMoveinDate", "DewaMoveoutDate", "DewaMoveoutcontractNumber" ),
		};
	}
	return data;
}

export function getTenantData( ctx: ITenantContractContext, tenantId: number ): Partial<TenancyContract> {
	const data: {
		Tenants: Tenant[];
	} = {
		Tenants: [],
	};
	let tenant: Tenant = {
		TenantParticipantId: 0,
		Identity: 0,
		TenantId: tenantId,
		IsPrimary: false,
	};
	if ( ctx.action === TenancyContactAction.RENEW_CONTRACT ) {
		const tenantInfo: Tenant = ctx.tenant!;
		tenant = { ...tenant, ...tenantInfo };
	}
	if ( ctx.isCommercial ) {
		const info: TCCommercialInfo = ctx.commercialInfo!;
		let person: PersonTenant = tenant.Person ? tenant.Person! : {};
		person = {
			...person,
			...Util.subSet( info, "LicenseIssuerEmirate", "LicenseIssuer", "LicenseStateId", "LicenseNo" ),
		};
		tenant = {
			...tenant,
			Person: person,
		};
	}
	data.Tenants.push( tenant );
	return data;
}

export function getContractData( ctx: ITenantContractContext ): Partial<TenancyContract> {
	const contractInfo = ctx.contractInfo!;
	let data = Util.subSet( contractInfo, "ContractStartDate", "ContractEndDate", "SecurityDeposit", "ContractAmount" );
	if ( contractInfo.IsGracePeriod === true ) {
		data = {
			...data,
			...Util.subSet( contractInfo, "GraceStartDate", "GraceEndDate" ),
		};
	}
	if ( contractInfo.ShowReason === true ) {
		data = {
			...data,
			...Util.subSet( contractInfo, "RegReasonId", "RegistrationReason" ),
		};
	}
	return data;
}

export function buildTenancyTerminate( ctx: ITenantContractContext ): TenantTerminateRequest {
	const tenantId = getAppServices().storeService.getStore().getState().User.tenantId;
	let terminateReq: TenantTerminateRequest = {
		RegistrationContractType: 2,
		ActiveUserType: TenantRequestUserType.TENANT,
		ContractId: ctx.contract!.TenancyContractId,
		ActiveUserID: "" + tenantId,
		OwnerRemarks: ctx.contract!.OwnerRemarks,
		Status: ctx.contract!.RequestActionType === TenantRequestAction.TERMINATE ? TenantRequestStatus.SUBMIT : 0,
	};
	terminateReq = {
		...terminateReq,
		...getRequesterData( ctx ),
	};
	terminateReq = normalizeTenantTerminate( terminateReq );
	return terminateReq;
}

export function getRequesterData( ctx: ITenantContractContext ): Partial<TenantTerminateRequest> {
	let tenant: Tenant = ctx.contract!.Tenants![ 0 ];
	const tenantPerson = tenant.Person!;
	const terminateInfo = ctx.terminateInfo!;
	return {
		RequesterEmiratesID: tenantPerson.EmiratesId,
		RequesterMobile: tenantPerson.Mobile,
		RequesterNameAr: tenant.TenantArabicName,
		RequesterNameEn: tenant.TenantEnglishName,
		...Util.subSet( terminateInfo, "RequesterEmail", "TerminationDate", "TerminationReasonId",
			"TerminationReasonText" ),
	};
}

export function buildTenancyContractFromRequest( ctx: ITenantContractContext, userTypeId: string, status: number,
                                                 remarks?: string ): TenancyContract {
	const details = ctx.contract!;
	const ownerId = EjariCoreConfig.getParticipantID()!;
	const tenantId = getAppServices().storeService.getStore().getState().User.tenantId;
	let contract: TenancyContract = {
		Tenants: details.Tenants,
		ContractValueType: details.ContractValueType,
		ContractAmount: details.ContractAmount,
		ContractStartDate: details.ContractStartDate,
		ContractEndDate: details.ContractEndDate,
		GraceStartDate: details.GraceStartDate,
		GraceEndDate: details.GraceEndDate,
		SecurityDeposit: details.SecurityDeposit,
		Remarks: details.Remarks,
		TenancyContractId: details.TenancyContractId,
		OwnerRemarks: ( remarks && !isEmptyStringOrNull( remarks ) ) ? remarks : details.OwnerRemarks,
		RequestType: 39,
		Status: status,
		ActiveUserType: Number( userTypeId ),
		ActiveUserID: "" + ( ( userTypeId === "" + TenantRequestUserType.OWNER ) ? ownerId : tenantId ),
	};
	if ( details.Properties && details.Properties.length > 0 ) {
		const property = details.Properties[ 0 ];
		const ejariPropertyId = ( property && property.EjariPropertyId ) ? property.EjariPropertyId : 0;
		contract = {
			...contract,
			...getHazardsMaterialData( ctx ),
			EjariPropertyId: [ ejariPropertyId ],
		};
	}
	if ( details.Status === TenantRequestStatus.REQUEST_FOR_UPDATE &&
		details.RequestActionType === TenantRequestAction.NEW ) {
		contract = {
			...contract,
			...getMoveOutData( ctx ),
		};
	}
	contract = normalizeTenantContract( contract );
	return contract;
}

export function buildTenancyTerminateFromRequest( ctx: ITenantContractContext, userTypeId: string, status: number,
                                                  remarks?: string ): TenantTerminateRequest {
	const details = ctx.contract!;
	const ownerId = EjariCoreConfig.getParticipantID()!;
	const tenantId = getAppServices().storeService.getStore().getState().User.tenantId;
	let terminateReq: TenantTerminateRequest = {
		RegistrationContractType: 2,
		ContractId: details.TenancyContractId,
		OwnerRemarks: ( remarks && !isEmptyStringOrNull( remarks ) ) ? remarks : details.OwnerRemarks,
		Status: status,
		ActiveUserType: Number( userTypeId ),
		ActiveUserID: "" + ( ( userTypeId === "" + TenantRequestUserType.OWNER ) ? ownerId : tenantId ),
	};
	if ( details.Tenants && details.Tenants.length > 0 ) {
		const tenant = details.Tenants[ 0 ];
		const person = tenant.Person;
		if ( person ) {
			terminateReq = {
				...terminateReq,
				RequesterEmiratesID: person.EmiratesId,
				RequesterMobile: person.Mobile,
				RequesterNameAr: tenant.TenantArabicName,
				RequesterNameEn: tenant.TenantEnglishName,
			};
		}
	}
	const tDetails = details.TerminationDetails;
	if ( tDetails ) {
		terminateReq = {
			...terminateReq,
			RequesterEmail: tDetails.RequesterEmail,
			TerminationDate: tDetails.TerminationDate,
			TerminationReasonId: tDetails.TerminationReasonId,
		};
	}
	return terminateReq;
}

function getHazardsMaterialData( ctx: ITenantContractContext ): Partial<TenancyContract> {
	return { HazardousMaterialClassRels: ctx.hazardIds };
}
